<script setup lang="ts">
import { mdiChevronDown, mdiChevronRight, mdiChevronUp, mdiOfficeBuilding } from "@mdi/js";
import { useToggle } from "@vueuse/core";
import type { components } from "~/api_types";
import { entityPath } from "~/utils/entityPath";

type BuildingsOverviewResponse = components["schemas"]["BuildingsOverviewResponse"];

const props = defineProps<{
  readonly buildings?: BuildingsOverviewResponse | null;
  readonly browseMapUrl: string;
}>();

const [buildingsExpanded, toggleBuildingsExpanded] = useToggle(false);
const { t } = useI18n({ useScope: "local" });
const runtimeConfig = useRuntimeConfig();
</script>

<template>
  <section v-if="props.buildings" class="print:!hidden">
    <div class="flex flex-row items-baseline justify-between gap-2 pb-3">
      <h2 class="text-zinc-800 dark:text-zinc-100 text-lg font-semibold">{{ t("title") }}</h2>
      <MapBridgeLink :to="props.browseMapUrl">{{ t("view_on_map") }}</MapBridgeLink>
    </div>
    <div class="text-zinc-600 dark:text-zinc-300 grid grid-cols-1 gap-3 lg:grid-cols-2">
      <template v-for="(b, i) in props.buildings.entries" :key="b.id">
        <NuxtLinkLocale
          v-if="i < props.buildings.n_visible || buildingsExpanded"
          :to="entityPath(b.id, b.type)"
          class="focusable border-zinc-200 dark:border-zinc-700 flex flex-row items-center justify-between rounded-sm border border-solid p-3.5 !no-underline hover:bg-zinc-100 dark:hover:bg-zinc-800"
          :aria-label="t('show_details_for', [b.name])"
        >
          <div class="flex flex-row items-center gap-3">
            <figure v-if="b.thumb" class="max-h-11 min-h-11 min-w-11">
              <NuxtImg
                width="64px"
                height="64px"
                class="aspect-square h-11 w-11 rounded-full"
                :alt="t('thumbnail_preview')"
                densities="x1 x2"
                :src="`${runtimeConfig.public.cdnURL}/cdn/thumb/${b.thumb}`"
              />
            </figure>
            <div v-else class="text-white dark:text-black bg-blue-500 dark:bg-blue-400 min-w-11 rounded-full p-2">
              <MdiIcon :path="mdiOfficeBuilding" :size="28" class="mx-auto" />
            </div>
            <div class="flex flex-col justify-evenly">
              <div class="line-clamp-2 text-balance">{{ b.name }}</div>
              <small class="text-zinc-600 dark:text-zinc-300">{{ b.subtext }}</small>
            </div>
          </div>
          <MdiIcon :path="mdiChevronRight" :size="16" />
        </NuxtLinkLocale>
      </template>
    </div>
    <div v-if="props.buildings.n_visible < props.buildings.entries.length" class="mt-2">
      <Btn
        variant="linkButton"
        :aria-label="buildingsExpanded ? t('show_less_buildings') : t('show_more_buildings')"
        @click="toggleBuildingsExpanded()"
      >
        <template v-if="buildingsExpanded">
          <MdiIcon :path="mdiChevronUp" :size="16" class="mt-0.5" />
          {{ t("less") }}
        </template>
        <template v-else>
          <MdiIcon :path="mdiChevronDown" :size="16" class="mt-0.5" />
          {{ t("more") }}
        </template>
      </Btn>
    </div>
  </section>
</template>

<i18n lang="yaml">
de:
  default_thumbnail_preview: Standard-Thumbnail, da kein Thumbnail verfügbar ist
  less: weniger
  more: mehr
  show_less_buildings: weniger Gebäude anzeigen
  show_more_buildings: mehr Gebäude anzeigen
  thumbnail_preview: Thumbnail, das eine Vorschau des Gebäudes zeigt
  title: Gebäude / Gebiete
  show_details_for: Details für das Gebäude '{0}' anzeigen
  view_on_map: Auf der Karte anzeigen
en:
  default_thumbnail_preview: Default-thumbnail, as no thumbnail is available
  less: less
  more: more
  show_less_buildings: show less buildings
  show_more_buildings: show more buildings
  thumbnail_preview: Thumbnail, showing a preview of the building
  title: Buildings / Areas
  show_details_for: show the details for the building '{0}'
  view_on_map: View on map
</i18n>
